// returningVisitor — 「この端末は前にも入室したことがあるか」を localStorage の記録から判定する。
//
// 「新機能の紹介」の入室時の自動表示 (docs/_wip/whats-new-2026-10-15.md「初回表示の条件」)
// で、初めての人には出さない判定に使う。
//
// 見る記録 (どちらも「入室に成功した」ときに初めて書かれる = 入室したことのある端末の印):
//   - `oekaki:displayName` — 入室に成功した表示名 (DisplayNameStore)
//   - `oekaki:anonUid`     — room.join 送信時に lazy 発行する匿名 ID (AnonUidStore)
// `oekaki:layout-onboarding-seen` は見ない (初回入室中の Modern 着地で書かれるうえ、
// Classic 着地では書かれないため判定材料として弱い)。
//
// **落とし穴**: これらのキーは初めての人でも「同じ訪問の中の」入室で書かれる。
// 入室した時点で見ると初めての人も常連になるので、必ず **アプリ起動時 (入室フォームより前)
// に一度だけ** 呼び、結果を保持すること (App が useRef で保持する)。
//
// 'unknown' = localStorage が使えない (どの記録も残らない → 毎回出てしまうのを防ぐため
// 呼び出し側は出さない)。

export type VisitorKind = 'returning' | 'first' | 'unknown';

export const RETURNING_VISITOR_KEYS: ReadonlyArray<string> = [
  'oekaki:displayName',
  'oekaki:anonUid',
];

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const probe = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:returning-visitor__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    return got === '1' ? ls : null;
  } catch {
    return null;
  }
};

export const detectVisitorKind = (storage?: StorageLike | null): VisitorKind => {
  const s = storage === undefined ? probe() : storage;
  if (s === null) return 'unknown';
  try {
    for (const key of RETURNING_VISITOR_KEYS) {
      const v = s.getItem(key);
      if (v !== null && v.trim().length > 0) return 'returning';
    }
    return 'first';
  } catch {
    return 'unknown';
  }
};
